import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { UnifiedInboxService } from '../unified-inbox.service';
import { NoteRequestAPI } from '../unified-inbox.models';

interface TemplateItem {
    templateId: number;
    name: string;
    body: string;
}

@Component({
    selector: 'app-note-composer',
    templateUrl: './note-composer.component.html',
    styleUrls: ['./note-composer.component.scss'],
    standalone: false
})
export class NoteComposerComponent implements OnChanges {
    @Input() conversationId: string;
    @Input() context: number = 7;
    @Input() toEmail: string = '';
    @Input() toMobile: string = '';
    @Input() lastMsgId: number = null;
    @Output() messageSent = new EventEmitter<void>();

    activeTab: 'email' | 'sms' | 'note' = 'email';
    subject = '';
    body = '';
    templateId: number = null;
    templates: TemplateItem[] = [];

    sending = false;
    sendError = '';
    charCount = 0;
    segmentCount = 1;

    suggesting = false;
    suggestError = '';

    constructor(private unifiedInboxService: UnifiedInboxService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['context']) {
            this.loadTemplates();
        }
        if (changes['conversationId']) {
            this.suggesting = false;
            this.suggestError = '';
        }
    }

    loadTemplates(): void {
        this.unifiedInboxService.getTemplates(this.context).subscribe({
            next: res => {
                const raw = res?.data?.messageTemplates;
                this.templates = Array.isArray(raw) ? raw : [];
            },
            error: () => { this.templates = []; }
        });
    }

    applyTemplate(id: number): void {
        const tpl = this.templates.find(t => t.templateId === id);
        if (tpl) {
            this.body = tpl.body;
            this.onBodyChange();
        }
    }

    selectTab(tab: 'email' | 'sms' | 'note'): void {
        this.activeTab = tab;
    }

    onBodyChange(): void {
        this.charCount = this.body.length;
        this.segmentCount = Math.max(1, Math.ceil(this.charCount / 160));
    }

    send(): void {
        if (!this.body.trim()) { return; }
        this.sending = true;
        this.sendError = '';

        if (this.activeTab === 'email') {
            const payload = {
                conversationId: this.conversationId,
                msgContext: this.context,
                toEmail: this.toEmail,
                subject: this.subject,
                body: this.body,
                templateId: this.templateId
            };
            this.unifiedInboxService.sendEmail(payload).subscribe({
                next: () => { this.sending = false; this.reset(); this.messageSent.emit(); },
                error: err => { this.sending = false; this.sendError = err?.error?.message || 'Failed to send email.'; }
            });
        } else if (this.activeTab === 'sms') {
            const payload = {
                conversationId: this.conversationId,
                msgContext: this.context,
                toMobile: this.toMobile,
                body: this.body
            };
            this.unifiedInboxService.sendSms(payload).subscribe({
                next: () => { this.sending = false; this.reset(); this.messageSent.emit(); },
                error: err => { this.sending = false; this.sendError = err?.error?.message || 'Failed to send SMS.'; }
            });
        } else if (this.activeTab === 'note') {
            const req: NoteRequestAPI = {
                msgConversationId: this.conversationId,
                body: this.body.trim(),
                msgContext: this.context
            };
            this.unifiedInboxService.postNote(req).subscribe({
                next: () => { this.sending = false; this.reset(); this.messageSent.emit(); },
                error: err => { this.sending = false; this.sendError = err?.error?.message || 'Failed to post note.'; }
            });
        }
    }

    private reset(): void {
        this.body = '';
        this.subject = '';
        this.templateId = null;
        this.charCount = 0;
        this.segmentCount = 1;
    }

    get hasMobile(): boolean {
        return !!(this.toMobile && this.toMobile.trim().length > 0);
    }

    suggestReply(): void {
        if (!this.conversationId || this.suggesting) { return; }
        this.suggesting = true;
        this.suggestError = '';
        const channel = this.activeTab === 'sms' ? 'SMS' : 'EMAIL';
        this.unifiedInboxService.suggestReply(this.conversationId, channel).subscribe({
            next: res => {
                this.suggesting = false;
                const draft = res?.data?.data?.draft || '';
                if (draft) {
                    this.body = draft;
                }
            },
            error: () => {
                this.suggesting = false;
                this.suggestError = 'Unable to suggest reply.';
            }
        });
    }
}
